/**
 * The side peek (1.I-2 deliverable 5): own markup on the kit's `cx-side-peek cx-raised` classes,
 * fixed below the header at the right edge, over the rail. A region layer: the editor's Esc comes
 * first and an Esc nothing consumed closes it (nothing here handles Esc).
 */
import { useLayer, usePortalContainer, useReturnFocus } from '@codex/ui';
import { lazy, Suspense, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { currentEntry, type PaneStack } from './pane-history.js';
import { LazyPaneDocument } from './pane-lazy.js';
import { PeekHeader, usePaneTitle } from './peek-header.js';

// The run sheet's source bar (2.D) reaches `@codex/doc-model`, the headless Tiptap schema, so it
// loads with the peek and never in the entry's static graph: `/login` loads no editor script
// (exit-2b gate red, `compose/entry-boundary.test.ts`).
const RunSheetPeekSource = lazy(() =>
  import('../sessions/drag/peek-source.js').then((m) => ({ default: m.RunSheetPeekSource })),
);

export interface PeekProps {
  readonly stack: PaneStack;
  readonly onClose: () => void;
  readonly onBack: () => void;
  readonly onForward: () => void;
  readonly onOpenAsPage: () => void;
  readonly onOpenInSplit?: (() => void) | undefined;
}

export function SidePeek({
  stack,
  onClose,
  onBack,
  onForward,
  onOpenAsPage,
  onOpenInSplit,
}: PeekProps) {
  const entry = currentEntry(stack);
  const title = usePaneTitle(entry.docId);
  const ref = useRef<HTMLElement>(null);
  // The token root is set at the first commit, long before a peek can open.
  const container = usePortalContainer() as HTMLElement;
  useLayer(true, onClose, 'side-peek', { region: ref });
  useReturnFocus(true);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return createPortal(
    <aside
      ref={ref}
      className="cx-side-peek cx-raised cx-nav-side"
      aria-label={`Side peek: ${title}`}
      data-testid="nav.peek.side"
      data-nav-pane="side"
      tabIndex={-1}
    >
      <PeekHeader
        title={title}
        stack={stack}
        history
        closeLabel="Close peek"
        onBack={onBack}
        onForward={onForward}
        onOpenAsPage={onOpenAsPage}
        onOpenInSplit={onOpenInSplit}
        onClose={onClose}
      />
      <Suspense fallback={null}>
        <RunSheetPeekSource docId={entry.docId} />
      </Suspense>
      <div className="cx-peek__body cx-nav-peek__body">
        <LazyPaneDocument docId={entry.docId} blockId={entry.blockId} source="side" />
      </div>
    </aside>,
    container,
  );
}
